import { ChevronRight } from '@mui/icons-material';
import { Card, CardContent, CardHeader, Grid, Typography } from '@mui/material';
import Colors from '@src/app/config/colorConfig';
import { useAppDispatch } from '@src/app/hooks/store';
import { selectOnePortfolioByKey } from '@src/app/store/reducers/portfolios';
import { numberFormatterDollar, percentageWithSign, positiveNegativeColor } from '@src/app/utils/GenericUtils';
import { holdingsUrl } from '@src/app/utils/UrlBuilder';
import { FC } from 'react';
import { useHistory } from 'react-router';

interface PerformanceCardProps {
  name: string;
  totalValue: number;
  capitalReturnValue: number;
  capitalReturnPercentage: number;
  incomeReturnValue: number;
  incomeReturnPercentage: number;
  totalReturnPercentage: number;
  totalReturnValue: number;
  portfolioKey: string;
}

interface PerformanceCardReturnProps {
  type: string;
  returnValue: number;
  returnPercent: number;
}

const PerformanceCardReturnColumn: FC<PerformanceCardReturnProps> = ({
  returnValue,
  returnPercent,
  type,
}: PerformanceCardReturnProps): JSX.Element => {
  return (
    <Grid item container spacing={1}>
      <Grid item xs={12} sm={8}>
        <Typography fontWeight={700} fontSize={14}>
          {type}
        </Typography>
      </Grid>
      <Grid item container spacing={1} xs={12} sm={4}>
        <Grid item>
          <Typography sx={{ color: positiveNegativeColor(returnValue) }} fontWeight={700} fontSize={14}>
            {numberFormatterDollar(returnValue)}
          </Typography>
        </Grid>
        <Grid item>
          <Typography sx={{ color: positiveNegativeColor(returnPercent) }} fontWeight={700} fontSize={14}>
            {`(${percentageWithSign(returnPercent)})`}
          </Typography>
        </Grid>
      </Grid>
    </Grid>
  );
};

export const PerformanceCard: FC<PerformanceCardProps> = ({
  totalValue,
  capitalReturnValue,
  capitalReturnPercentage,
  incomeReturnValue,
  incomeReturnPercentage,
  totalReturnPercentage,
  totalReturnValue,
  portfolioKey,
  name,
}: PerformanceCardProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const history = useHistory();

  const selectPortfolio = () => {
    dispatch(selectOnePortfolioByKey(portfolioKey));
  };

  const handleNavigation = () => {
    selectPortfolio();
    history.push(holdingsUrl());
  };

  return (
    <Card
      onClick={handleNavigation}
      elevation={0}
      sx={{
        borderRadius: '13.85px',
        border: `1px solid ${Colors.gray300}`,
        boxShadow: '0px 1.84px 10.11px 1.84px #E4E4E4',
        ':hover': {
          cursor: 'pointer',
        },
        minHeight: '100%',
      }}
    >
      <CardHeader
        title={name}
        titleTypographyProps={{
          fontWeight: 700,
          textTransform: 'uppercase',
          fontSize: 18,
          color: '#C1C1C1',
        }}
        action={<ChevronRight sx={{ color: '#D7D7D7' }} />}
      />
      <CardContent>
        <Grid container direction="column" spacing={3}>
          <Grid item>
            <Typography fontWeight={700} fontSize={18}>
              {numberFormatterDollar(totalValue)}
            </Typography>
          </Grid>
          <Grid item container direction="column" spacing={1}>
            <PerformanceCardReturnColumn type="Capital Return" returnValue={capitalReturnValue || 0} returnPercent={capitalReturnPercentage || 0} />
            <PerformanceCardReturnColumn type="Income Return" returnValue={incomeReturnValue || 0} returnPercent={incomeReturnPercentage || 0} />
            <PerformanceCardReturnColumn type="Total Return" returnValue={totalReturnValue || 0} returnPercent={totalReturnPercentage || 0} />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};
